import { HttpsCallable, httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import { useCallback, useState } from "react";

export function createHttpsCallableHook<
  TAPIRequest,
  TAPIResponse,
  TRequest = TAPIRequest,
  TResponse = TAPIResponse,
>(
  functionName: string,
  middlewareOnCall?: (request: TRequest) => TAPIRequest,
  middlewareOnResult?: (result: TAPIResponse) => TResponse,
) {
  return () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<TResponse | null>(null);

    const apiCall: HttpsCallable<TAPIRequest, TAPIResponse> = useCallback(
      httpsCallable<TAPIRequest, TAPIResponse>(functions, functionName),
      [functions, functionName],
    );

    const call = useCallback(
      (
        request: TRequest,
        middlewareOnCallInternal = middlewareOnCall,
        middlewareOnResultInternal = middlewareOnResult,
      ) => {
        let internalRequest: TRequest | TAPIRequest = request;
        if (middlewareOnCallInternal) {
          internalRequest = middlewareOnCallInternal(request);
        }
        setLoading(true);
        return apiCall(internalRequest as TAPIRequest)
          .then((result) => {
            let internalResult: TResponse | TAPIResponse =
              result.data as TAPIResponse;
            if (middlewareOnResultInternal) {
              internalResult = middlewareOnResultInternal(result.data);
            }
            setResult(internalResult as TResponse);
            return internalResult;
          })
          .catch((error) => {
            setError(error.message || "Unknown error");
            setResult(null);
            throw error;
          })
          .finally(() => {
            setLoading(false);
          });
      },
      [apiCall],
    );

    return [call, result, loading, error, setResult] as [
      (
        request: TRequest,
        middlewareOnCall?: (request: TRequest) => TAPIRequest,
        middlewareOnResult?: (result: TAPIResponse) => TResponse,
      ) => Promise<TResponse>,
      TResponse | null,
      boolean,
      string | null,
      (result: TResponse | null) => void,
    ];
  };
}
